import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useVersionedApi } from '../../utils/versionedApi';
import { useDebounce } from '../../hooks/useDebounce';
import { safeStorage } from '../../utils/safeStorage';

// All persistence goes through safeStorage: the reads were already try/caught
// but the writes (setItem) were not, so a quota/security exception (Safari
// lockdown, full storage) thrown from addRecentPatient/toggleFavoritePatient
// propagated into the click handler and broke patient navigation from the
// palette. safeStorage swallows storage errors for both get and set.
const SEARCH_HISTORY_KEY = 'arasync_search_history';

const getSearchHistory = () => {
  try { return JSON.parse(safeStorage.getItem(SEARCH_HISTORY_KEY) || '[]'); }
  catch { return []; }
};

const addSearchHistory = (query) => {
  if (!query || query.length < 2) return;
  const history = getSearchHistory().filter(q => q !== query);
  history.unshift(query);
  safeStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(history.slice(0, 5)));
};

const RECENT_KEY = 'arasync_recent_patients';
const MAX_RECENT = 8;

const getRecentPatients = () => {
  try {
    return JSON.parse(safeStorage.getItem(RECENT_KEY) || '[]');
  } catch { return []; }
};

const addRecentPatient = (patient) => {
  const recent = getRecentPatients().filter(p => p.id !== patient.id);
  recent.unshift({ id: patient.id, label: patient.label, subtitle: patient.subtitle });
  safeStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
};

const FAVORITES_KEY = 'arasync_favorite_patients';

const getFavoritePatients = () => {
  try {
    return JSON.parse(safeStorage.getItem(FAVORITES_KEY) || '[]');
  } catch { return []; }
};

const toggleFavoritePatient = (patient) => {
  const favorites = getFavoritePatients();
  const exists = favorites.find(p => p.id === patient.id);
  if (exists) {
    const updated = favorites.filter(p => p.id !== patient.id);
    safeStorage.setItem(FAVORITES_KEY, JSON.stringify(updated));
    return false; // removed
  } else {
    favorites.unshift({ id: patient.id, label: patient.label, subtitle: patient.subtitle });
    safeStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites.slice(0, 10)));
    return true; // added
  }
};

const isFavorite = (patientId) => {
  return getFavoritePatients().some(p => p.id === patientId);
};

const STATIC_COMMANDS = [
  { id: 'dashboard', label: 'Dashboard', path: '/admin', section: 'Pages', icon: 'H' },
  { id: 'patients', label: 'Patients', path: '/admin/patients', section: 'Pages', icon: 'P' },
  { id: 'scheduler', label: 'Scheduler', path: '/admin/scheduler', section: 'Pages', icon: 'S' },
  { id: 'billing', label: 'Billing', path: '/admin/billing', section: 'Pages', icon: 'B' },
  { id: 'clinical', label: 'Clinical Documents', path: '/admin/clinical', section: 'Pages', icon: 'C' },
  { id: 'visits', label: 'Visit Logs', path: '/admin/programs/home-health?tab=visit-logs', section: 'Pages', icon: 'V' },
  { id: 'reports', label: 'Reports', path: '/admin/reports', section: 'Pages', icon: 'R' },
  { id: 'settings', label: 'Settings', path: '/admin/settings', section: 'Pages', icon: 'G' },
  { id: 'caregivers', label: 'Caregivers', path: '/admin/caregivers', section: 'Pages', icon: 'C' },
  { id: 'alerts', label: 'Alerts', path: '/admin/alerts', section: 'Pages', icon: 'A' },
  { id: 'waitlist', label: 'Waitlist', path: '/admin/waitlist', section: 'Pages', icon: 'W' },
  { id: 'compliance', label: 'Compliance', path: '/admin/compliance', section: 'Pages', icon: 'C' },
];

// NOTE: `navigate` must come from useOrganizationNavigation() in AdminShell.
const CommandPalette = ({ isOpen, onClose, navigate, hiddenCommandIds = [] }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [patientResults, setPatientResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [favVersion, setFavVersion] = useState(0);
  const [histVersion, setHistVersion] = useState(0);
  const inputRef = useRef(null);

  const apiClient = useVersionedApi('v1');
  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setPatientResults([]);
      return;
    }
    let cancelled = false;
    const searchPatients = async () => {
      setIsSearching(true);
      try {
        const response = await apiClient.get('/patients/suggestions', {
          params: { q: debouncedQuery, limit: 5 }
        });
        if (!cancelled) {
          setPatientResults(Array.isArray(response.data) ? response.data : []);
        }
      } catch {
        if (!cancelled) setPatientResults([]);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };
    searchPatients();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  const allResults = useMemo(() => {
    const hidden = new Set(hiddenCommandIds);
    const visibleCommands = STATIC_COMMANDS.filter((cmd) => !hidden.has(cmd.id));
    const staticFiltered = !query.trim() ? visibleCommands :
      visibleCommands.filter(cmd =>
        cmd.label.toLowerCase().includes(query.toLowerCase()) ||
        cmd.section.toLowerCase().includes(query.toLowerCase())
      );

    const patientItems = patientResults.map(p => ({
      id: `patient-${p.id}`,
      label: p.fullName,
      path: '/admin/patients',
      section: 'Patients',
      icon: 'P',
      patientId: p.id,
      subtitle: p.displayText || p.phone || '',
    }));

    const recentItems = !query.trim() ? getRecentPatients().map(p => ({
      id: `recent-${p.id}`,
      label: p.label,
      path: '/admin/patients',
      section: 'Recent Patients',
      icon: 'R',
      patientId: p.id,
      subtitle: p.subtitle || '',
    })) : [];

    const favoriteItems = !query.trim() ? getFavoritePatients().map(p => ({
      id: `fav-${p.id}`,
      label: p.label,
      path: '/admin/patients',
      section: 'Favorites',
      icon: '★',
      patientId: p.id,
      subtitle: p.subtitle || '',
      isFavorite: true,
    })) : [];

    const searchHistoryItems = !query.trim() ? getSearchHistory().map(q => ({
      id: `history-${q}`,
      label: q,
      section: 'Recent Searches',
      icon: '⟳',
      isSearchHistory: true,
      historyQuery: q,
    })) : [];

    return [...staticFiltered, ...favoriteItems, ...searchHistoryItems, ...recentItems, ...patientItems];
  }, [query, patientResults, favVersion, histVersion, hiddenCommandIds]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOpen) { setQuery(''); setSelectedIndex(0); setPatientResults([]); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [isOpen]);

  useEffect(() => { setSelectedIndex(0); }, [query]);

  const executeCommand = useCallback((cmd) => {
    if (cmd.isSearchHistory) {
      setQuery(cmd.historyQuery);
      return;
    }
    if (cmd.patientId) {
      addRecentPatient({ id: cmd.patientId, label: cmd.label, subtitle: cmd.subtitle });
      addSearchHistory(query);
      setHistVersion(v => v + 1);
      navigate('/admin/patients');
    } else if (cmd.path) {
      navigate(cmd.path);
    }
    onClose();
  }, [navigate, onClose, query]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => i < allResults.length - 1 ? i + 1 : 0); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => i > 0 ? i - 1 : allResults.length - 1); }
    else if (e.key === 'Enter' && allResults[selectedIndex]) { e.preventDefault(); executeCommand(allResults[selectedIndex]); }
    else if (e.key === 'Escape') { onClose(); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-modal-overlay" aria-hidden="true" />
      <div
        role="combobox"
        aria-expanded="true"
        aria-haspopup="listbox"
        aria-label="Command palette"
        className="relative w-full max-w-xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center px-4 border-b border-gray-200 dark:border-gray-700">
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            aria-label="Search commands"
            aria-autocomplete="list"
            className="w-full max-w-full box-border px-3 py-3 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="px-1.5 py-0.5 text-xs text-gray-400 border border-gray-300 dark:border-gray-600 rounded">esc</kbd>
        </div>
        <div role="listbox" className="max-h-80 overflow-y-auto py-2">
          {isSearching && (
            <p className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500">Searching patients...</p>
          )}
          {!isSearching && allResults.length === 0 && (
            <p className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">No results found</p>
          )}
          {allResults.map((cmd, index) => (
            <div
              key={cmd.id}
              role="option"
              aria-selected={index === selectedIndex}
              className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer text-sm ${
                index === selectedIndex
                  ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              onClick={() => executeCommand(cmd)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="w-6 h-6 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs font-sans text-gray-500 dark:text-gray-400">{cmd.icon}</span>
              <span className="flex-1">
                {cmd.label}
                {cmd.subtitle && (
                  <span className="block text-xs text-gray-400 dark:text-gray-500">{cmd.subtitle}</span>
                )}
              </span>
              <span className="text-xs text-gray-400">{cmd.section}</span>
              {cmd.patientId && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavoritePatient({ id: cmd.patientId, label: cmd.label, subtitle: cmd.subtitle });
                    setFavVersion(v => v + 1);
                  }}
                  aria-label={isFavorite(cmd.patientId) ? 'Remove from favorites' : 'Add to favorites'}
                  className="p-0.5 text-gray-400 hover:text-yellow-500 dark:hover:text-yellow-400"
                >
                  {isFavorite(cmd.patientId) ? '★' : '☆'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
